import type { ComponentType } from 'react';

import type { TFunction } from 'i18next';

import { TarjetaAhorro } from '#components/mobile/informes/tarjetas/TarjetaAhorro';
import { TarjetaPatrimonio } from '#components/mobile/informes/tarjetas/TarjetaPatrimonio';
import { TarjetaRitmo } from '#components/mobile/informes/tarjetas/TarjetaRitmo';
import {
  TarjetaCategoria,
  TarjetaDiario,
  TarjetaIngresos,
  TarjetaMargen,
  TarjetaSemana,
} from '#components/mobile/informes/tarjetas/TarjetasCatalogo';
import { TarjetaSePasan } from '#components/mobile/informes/tarjetas/TarjetaSePasan';
import { TarjetaSeVa } from '#components/mobile/informes/tarjetas/TarjetaSeVa';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import type { IdWidget } from '#components/mobile/inicio/modeloWidgets';

import { ABordo } from './ABordo';
import { Consejos } from './Consejos';
import { CuentaComun } from './CuentaComun';
import { Deudas } from './Deudas';
import { Fijadas } from './Fijadas';
import { GastoMes } from './GastoMes';
import { Hoy } from './Hoy';
import { Huchas } from './Huchas';
import { MesesCubiertos } from './MesesCubiertos';
import { PagasExtra } from './PagasExtra';
import { PagosFijos } from './PagosFijos';
import { PorHacer } from './PorHacer';
import { ResumenMes } from './ResumenMes';

export const COMPONENTES_WIDGET: Record<
  IdWidget,
  ComponentType<PropsWidget>
> = {
  hoy: Hoy,
  'por-hacer': PorHacer,
  fijadas: Fijadas,
  'cuenta-comun': CuentaComun,
  consejos: Consejos,
  'resumen-mes': ResumenMes,
  deudas: Deudas,
  'gasto-mes': GastoMes,
  huchas: Huchas,
  'meses-cubiertos': MesesCubiertos,
  'pagas-extra': PagasExtra,
  'a-bordo': ABordo,
  'pagos-fijos': PagosFijos,
  // Tarjetas de Informes
  'informe-ritmo': TarjetaRitmo,
  'informe-se-va': TarjetaSeVa,
  'informe-se-pasan': TarjetaSePasan,
  'informe-ahorro': TarjetaAhorro,
  'informe-patrimonio': TarjetaPatrimonio,
  'informe-semana': TarjetaSemana,
  'informe-categoria': TarjetaCategoria,
  'informe-ingresos': TarjetaIngresos,
  'informe-margen': TarjetaMargen,
  'informe-diario': TarjetaDiario,
};

export function nombreWidget(t: TFunction, id: IdWidget): string {
  switch (id) {
    case 'hoy':
      return t('Today');
    case 'por-hacer':
      return t('To do');
    case 'fijadas':
      return t('Pinned');
    case 'cuenta-comun':
      return t('Joint account');
    case 'consejos':
      return t("Gavi's advice");
    case 'resumen-mes':
      return t('Month summary');
    case 'deudas':
      return t('Debts');
    case 'gasto-mes':
      return t('Spending this month');
    case 'huchas':
      return t('Piggy banks');
    case 'meses-cubiertos':
      return t('Months covered');
    case 'pagas-extra':
      return t('Extra payments');
    case 'a-bordo':
      return t('Aboard');
    case 'pagos-fijos':
      return t('Fixed payments');
    case 'informe-ritmo':
      return t('How the month is going');
    case 'informe-se-va':
      return t('Where it goes');
    case 'informe-se-pasan':
      return t('Going over');
    case 'informe-ahorro':
      return t('Savings');
    case 'informe-patrimonio':
      return t('What you have');
    case 'informe-semana':
      return t('Your week');
    case 'informe-categoria':
      return t('A category');
    case 'informe-ingresos':
      return t('Income');
    case 'informe-margen':
      return t('Days of margin');
    case 'informe-diario':
      return t("Ship's log");
    default:
      return id;
  }
}

/**
 * Qué cuenta cada tarjeta y cómo se usa: la misma explicación sale en el
 * catálogo «Añadir tarjeta» y cuando Gavi la explica («Sobre una de mis
 * tarjetas»).
 */
export function descripcionWidget(t: TFunction, id: IdWidget): string {
  switch (id) {
    case 'hoy':
      return t(
        'What you can spend today on your day-to-day without overspending the month. With Add expense you note it in two taps; in Categories you choose what counts.',
      );
    case 'por-hacer':
      return t(
        'What needs your attention: transactions to review, categories in the red, money to assign or accounts to reconcile. Each row has its own button; when everything is up to date, it stays quiet.',
      );
    case 'fijadas':
      return t(
        'The categories that matter most to you, with what they have left: the ring fills up as you spend. You choose them in Choose pinned.',
      );
    case 'cuenta-comun':
      return t(
        'How much to put in the joint account for next month. When you transfer it, tap Transferred and it is covered.',
      );
    case 'consejos':
      return t(
        'My advice from the analysis of your month. See all opens every piece.',
      );
    case 'resumen-mes':
      return t(
        'The income, the assigned and the spent of the month, with a bar to see it at a glance.',
      );
    case 'deudas':
      return t('Your loans: how much you have paid and when you finish.');
    case 'gasto-mes':
      return t(
        'What you have spent this month, split by category in a bar of colours. See opens the report.',
      );
    case 'huchas':
      return t(
        'The money set aside outside the budget (savings, investments) and its balance.',
      );
    case 'meses-cubiertos':
      return t(
        'How many months you already have paid in advance. Each tank is a month: full, that month is covered. Assign fills the next one.',
      );
    case 'pagas-extra':
      return t(
        'When your extra payments arrive and how many days are left. It only informs: it does not plan money you do not have yet. The day it arrives, I suggest how to split it.',
      );
    case 'a-bordo':
      return t(
        'The latest expenses of the two of you, with who added each one.',
      );
    case 'pagos-fijos':
      return t(
        'What is left to pay of your bills this month and the next charge.',
      );
    case 'informe-ritmo':
      return t(
        'What is left of your plan this month and whether you are on track, tight or overspending. Large adds your spending day by day against a normal pace.',
      );
    case 'informe-se-va':
      return t(
        'What you have spent this month, split by category in a bar of colours. Large adds the four biggest with how they changed.',
      );
    case 'informe-se-pasan':
      return t(
        'The categories already in the red and those that will go over at this pace before the month ends.',
      );
    case 'informe-ahorro':
      return t(
        'How much of what came in you kept last month, compared with your average, and what came in and went out in recent months.',
      );
    case 'informe-patrimonio':
      return t(
        'Your accounts minus your debts, and how it changed since last month.',
      );
    case 'informe-semana':
      return t(
        'What you spent last week, day by day, compared with a normal week.',
      );
    case 'informe-categoria':
      return t(
        'Your biggest category this month against its last 12 months. Tap it to choose any other category and period.',
      );
    case 'informe-ingresos':
      return t('What came in this month compared with a normal month.');
    case 'informe-margen':
      return t(
        'How many days you live on money you earned before: the more, the calmer.',
      );
    case 'informe-diario':
      return t(
        'Your last month told by me: what came in, what went out and what you kept.',
      );
    default:
      return '';
  }
}
